import { useUser } from './useUser';

type PtoLabelConfig = {
  overrides: Record<string, string>;
};

// Static per-client overrides. We have to allow admins to override the labels for the time off types for their client
// before we can promote this to a server-side company config setting.
const CLIENT_PTO_LABEL_CONFIGS: Record<string, PtoLabelConfig> = {
  '623278': {
    overrides: {
      WFO: 'WFO',
      SICKCA: 'SICK',
      SICK: 'SICK',
      VACATION: 'PTO',
    },
  },
};

export function getPtoLabelConfig(clientId: string): PtoLabelConfig {
  return CLIENT_PTO_LABEL_CONFIGS[clientId] ?? { overrides: {} };
}

/**
 * Returns the label to use in place of "PTO" for calendar/card event titles.
 * e.g. "${name} PTO" becomes "${name} WFM" when overridden.
 */
export function getPtoEventLabel(
  config: PtoLabelConfig,
  leaveType: string
): string {
  return config.overrides[leaveType] ?? 'PTO';
}

/**
 * Returns the full title for a time-off request entry.
 * Overridden types return just the label ("WFM"); standard types
 * return "${leaveType} Time Off" (e.g. "Vacation Time Off").
 */
export function getTimeOffTitle(
  config: PtoLabelConfig,
  leaveType: string
): string {
  return config.overrides[leaveType] ?? `${leaveType} Time Off`;
}

// Main hook
export function usePtoLabel() {
  const { entity } = useUser();
  const config = getPtoLabelConfig(entity?.id ?? '');

  return {
    getPtoEventLabel: (leaveType: string) =>
      getPtoEventLabel(config, leaveType),
    getTimeOffTitle: (leaveType: string) => getTimeOffTitle(config, leaveType),
  };
}
